import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AccountFinal, PersonFinal } from '@/api/entities';
import { Search, Save, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

export default function UniversalFieldEditor() {
  const [entityType, setEntityType] = useState('AccountFinal');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editedFields, setEditedFields] = useState({});
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setMessage({ type: 'error', text: 'Please enter a search query' });
      return;
    }

    setIsSearching(true);
    setMessage(null);
    
    try {
      let results = [];
      
      if (entityType === 'AccountFinal') {
        const allAccounts = await AccountFinal.list();
        const query = searchQuery.toLowerCase();
        results = allAccounts.filter(acc => 
          acc.account_name?.toLowerCase().includes(query) ||
          acc.account_id?.toLowerCase().includes(query) ||
          acc.id?.toLowerCase().includes(query)
        ).slice(0, 10);
      } else if (entityType === 'PersonFinal') {
        const allPeople = await PersonFinal.list();
        const query = searchQuery.toLowerCase();
        results = allPeople.filter(person => 
          `${person.first_name} ${person.last_name}`.toLowerCase().includes(query) ||
          person.email?.toLowerCase().includes(query) ||
          person.person_id?.toLowerCase().includes(query) ||
          person.id?.toLowerCase().includes(query)
        ).slice(0, 10);
      }
      
      setSearchResults(results);
      setSelectedRecord(null);
      setEditedFields({});
      
      if (results.length === 0) {
        setMessage({ type: 'info', text: 'No results found' });
      }
    } catch (error) {
      console.error('Search error:', error);
      setMessage({ type: 'error', text: `Search failed: ${error.message}` });
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectRecord = (record) => {
    setSelectedRecord(record);
    setEditedFields({});
    setMessage(null);
  };

  const handleFieldChange = (fieldName, value) => {
    setEditedFields(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleSave = async () => {
    if (!selectedRecord || Object.keys(editedFields).length === 0) {
      setMessage({ type: 'error', text: 'No changes to save' });
      return;
    }

    setIsSaving(true);
    setMessage(null);

    try {
      console.log('Saving changes:', { id: selectedRecord.id, changes: editedFields });
      
      if (entityType === 'AccountFinal') {
        const updated = await AccountFinal.update(selectedRecord.id, editedFields);
        console.log('Update response:', updated);
      } else if (entityType === 'PersonFinal') {
        const updated = await PersonFinal.update(selectedRecord.id, editedFields);
        console.log('Update response:', updated);
      }

      // Clear all relevant caches
      try {
        sessionStorage.removeItem('home-cache-v2');
        sessionStorage.removeItem('accounts-cache-v2');
        sessionStorage.removeItem('people-cache-v2');
        const cacheKey = entityType === 'AccountFinal' 
          ? `account-final-${selectedRecord.id}`
          : `person-profile-v2-${selectedRecord.id}`;
        sessionStorage.removeItem(cacheKey);
        console.log('Caches cleared');
      } catch (e) {
        console.warn('Cache clear failed:', e);
      }

      // Update the selected record with new values
      setSelectedRecord(prev => ({ ...prev, ...editedFields }));
      setEditedFields({});
      setMessage({ type: 'success', text: 'Changes saved successfully! Refresh the page to see updates.' });

      // Update search results
      setSearchResults(prev => 
        prev.map(r => r.id === selectedRecord.id ? { ...r, ...editedFields } : r)
      );
    } catch (error) {
      console.error('Save error:', error);
      setMessage({ type: 'error', text: `Save failed: ${error.message}` });
    } finally {
      setIsSaving(false);
    }
  };

  const renderFieldEditor = (fieldName, currentValue) => {
    const value = editedFields.hasOwnProperty(fieldName) ? editedFields[fieldName] : currentValue;

    // Text areas for long fields
    if (['score_rationale', 'decision_window_rationale', 'bvo_rationale', 
         'incumbent_sentiment_rationale', 'persona_score_rationale'].includes(fieldName)) {
      return (
        <Textarea
          value={value || ''}
          onChange={(e) => handleFieldChange(fieldName, e.target.value)}
          className="min-h-[100px]"
        />
      );
    }

    // Number inputs
    if (['account_fit_score', 'persona_fit_score', 'momentum_4w', 'momentum_12w', 
         'procurement_influence_score', 'social_presence_score', 'enrolment', 'employees'].includes(fieldName)) {
      return (
        <Input
          type="number"
          value={value || ''}
          onChange={(e) => handleFieldChange(fieldName, parseFloat(e.target.value) || 0)}
        />
      );
    }

    // Arrays (display as comma-separated)
    if (Array.isArray(currentValue)) {
      return (
        <Textarea
          value={Array.isArray(value) ? value.join(', ') : value || ''}
          onChange={(e) => handleFieldChange(fieldName, e.target.value.split(',').map(s => s.trim()))}
          placeholder="Enter comma-separated values"
        />
      );
    }

    // Default text input
    return (
      <Input
        value={value || ''}
        onChange={(e) => handleFieldChange(fieldName, e.target.value)}
      />
    );
  };

  const getDisplayName = (record) => {
    if (entityType === 'AccountFinal') {
      return record.account_name;
    } else if (entityType === 'PersonFinal') {
      return `${record.first_name} ${record.last_name}`;
    }
    return 'Unknown';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Search className="w-5 h-5" />
          Universal Field Editor
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {message && (
          <Alert variant={message.type === 'error' ? 'destructive' : 'default'}>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{message.text}</AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Entity Type</Label>
            <Select value={entityType} onValueChange={setEntityType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="AccountFinal">Accounts</SelectItem>
                <SelectItem value="PersonFinal">People</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="md:col-span-2">
            <Label>Search</Label>
            <div className="flex gap-2">
              <Input
                placeholder={entityType === 'AccountFinal' ? 'Search by name or ID...' : 'Search by name, email, or ID...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button onClick={handleSearch} disabled={isSearching}>
                {isSearching ? 'Searching...' : 'Search'}
              </Button>
            </div>
          </div>
        </div>

        {searchResults.length > 0 && (
          <div>
            <Label>Results ({searchResults.length})</Label>
            <div className="border rounded-md divide-y max-h-[200px] overflow-y-auto">
              {searchResults.map(record => (
                <button
                  key={record.id}
                  onClick={() => handleSelectRecord(record)}
                  className={`w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors ${
                    selectedRecord?.id === record.id ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="font-medium">{getDisplayName(record)}</div>
                  {entityType === 'AccountFinal' && (
                    <div className="text-xs text-gray-500">{record.country} • {record.tenant_id}</div>
                  )}
                  {entityType === 'PersonFinal' && (
                    <div className="text-xs text-gray-500">{record.account_name} • {record.role_title}</div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {selectedRecord && (
          <div className="border-t pt-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">{getDisplayName(selectedRecord)}</h3>
              <Button onClick={handleSave} disabled={isSaving || Object.keys(editedFields).length === 0}>
                <Save className="w-4 h-4 mr-2" />
                {isSaving ? 'Saving...' : `Save ${Object.keys(editedFields).length} changes`}
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[600px] overflow-y-auto">
              {Object.entries(selectedRecord)
                .filter(([key]) => !['id', 'created_at', 'updated_at', 'created_by'].includes(key))
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([fieldName, currentValue]) => (
                  <div key={fieldName}>
                    <Label className="text-xs">
                      {fieldName}
                      {editedFields.hasOwnProperty(fieldName) && (
                        <span className="ml-2 text-blue-600 font-semibold">*</span>
                      )}
                    </Label>
                    {renderFieldEditor(fieldName, currentValue)}
                    {!editedFields.hasOwnProperty(fieldName) && currentValue && (
                      <p className="text-xs text-gray-500 mt-1 truncate">
                        Current: {Array.isArray(currentValue) ? currentValue.join(', ') : String(currentValue).substring(0, 100)}
                      </p>
                    )}
                  </div>
                ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}